'use client';
import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { supabase } from '@/lib/supabase';
import ReviewsManager from './components/ReviewsManager';

const ADMIN_USERNAME = 'trueblu26';
const ADMIN_PASSWORD = 'trueblu2026';

interface WaitlistEntry {
  id: string;
  email: string;
  location: string;
  vehicle_year: string | null;
  vehicle_make: string | null;
  vehicle_model: string | null;
  vehicle_type: string | null;
  notes: string | null;
  created_at: string;
}

interface BookingEntry {
  id: string;
  email: string | null;
  phone: string | null;
  vehicle_year: string | null;
  vehicle_make: string | null;
  vehicle_model: string | null;
  service: string | null;
  service_price: string | null;
  requested_date: string | null;
  photo_file_name: string | null;
  submission_type: string;
  status: string;
  created_at: string;
}

type TabType = 'waitlist' | 'bookings' | 'campaigns' | 'content' | 'traffic';

// ── Campaign sender component ────────────────────────────────────────────────
interface CampaignForm {
  subject: string;
  headline: string;
  bodyText: string;
  ctaText: string;
  ctaUrl: string;
  teaserNumber: string;
}

const defaultTeaser: CampaignForm = {
  subject: '🔥 Something premium is almost here — Vertex Premium',
  headline: 'Something premium is coming.',
  bodyText: "We're putting the finishing touches on Vertex Premium Mobile Detailing. Your spot is locked in — and you're going to love what we've built. Stay tuned for the official launch announcement.",
  ctaText: 'Follow Us on Instagram',
  ctaUrl: 'https://www.instagram.com/vertexpremiummobiledetailing',
  teaserNumber: '1',
};

const defaultReminder: CampaignForm = {
  subject: '📅 Your Vertex Premium booking — we\'re almost ready',
  headline: 'Your appointment is coming up.',
  bodyText: "We're getting closer to launch and wanted to remind you that your advance booking is confirmed. We'll reach out shortly before your scheduled date to lock in the exact time. Thank you for booking early — your 15% discount is secured.",
  ctaText: 'DM Us on Instagram',
  ctaUrl: 'https://www.instagram.com/vertexpremiummobiledetailing',
  teaserNumber: '',
};

function CampaignPanel({ supabaseUrl }: { supabaseUrl: string }) {
  const [campaignType, setCampaignType] = useState<'teaser' | 'reminder'>('teaser');
  const [form, setForm] = useState<CampaignForm>(defaultTeaser);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ sent?: number; total?: number; errors?: string[]; message?: string } | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  const handleTypeSwitch = (type: 'teaser' | 'reminder') => {
    setCampaignType(type);
    setForm(type === 'teaser' ? defaultTeaser : defaultReminder);
    setResult(null);
    setSendError(null);
  };

  const handleSend = async () => {
    setSending(true);
    setResult(null);
    setSendError(null);
    try {
      const fnName = campaignType === 'teaser' ? 'send-teaser-email' : 'send-reminder-email';
      const payload: Record<string, string> = {
        subject: form.subject,
        headline: form.headline,
        bodyText: form.bodyText,
        ctaText: form.ctaText,
        ctaUrl: form.ctaUrl,
      };
      if (campaignType === 'teaser' && form.teaserNumber) {
        payload.teaserNumber = form.teaserNumber;
      }
      const res = await fetch(`${supabaseUrl}/functions/v1/${fnName}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || ''}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Send failed');
      setResult(data);
    } catch (e: unknown) {
      setSendError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setSending(false);
    }
  };

  const inputStyle = {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(0,245,255,0.12)',
    color: '#F5F5F7',
    borderRadius: 10,
    padding: '10px 14px',
    fontSize: 14,
    width: '100%',
    outline: 'none',
    boxSizing: 'border-box' as const,
  };

  const labelStyle = {
    fontSize: 10,
    letterSpacing: '0.2em',
    textTransform: 'uppercase' as const,
    color: 'rgba(168,169,173,0.5)',
    fontWeight: 600,
    display: 'block',
    marginBottom: 6,
    fontFamily: 'monospace',
  };

  return (
    <div className="space-y-6">
      {/* Type selector */}
      <div
        className="flex gap-1 p-1 rounded-xl w-fit"
        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(168,169,173,0.08)' }}
      >
        {(['teaser', 'reminder'] as const).map((t) => (
          <button
            key={t}
            onClick={() => handleTypeSwitch(t)}
            className="px-4 py-2 rounded-lg mono text-[10px] font-semibold tracking-widest uppercase transition-all duration-200"
            style={
              campaignType === t
                ? {
                    background: 'linear-gradient(135deg, rgba(0,245,255,0.15) 0%, rgba(123,47,255,0.15) 100%)',
                    border: '1px solid rgba(0,245,255,0.3)',
                    color: '#00f5ff',
                  }
                : {
                    background: 'transparent',
                    border: '1px solid transparent',
                    color: 'rgba(168,169,173,0.45)',
                  }
            }
          >
            {t === 'teaser' ? '🔥 Pre-Launch Teaser' : '📅 Booking Reminder'}
          </button>
        ))}
      </div>

      {/* Description */}
      <div
        className="rounded-xl p-4"
        style={{ background: 'rgba(0,245,255,0.04)', border: '1px solid rgba(0,245,255,0.1)' }}
      >
        <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>
          {campaignType === 'teaser' ?'📣 Sends a teaser email to all waitlist subscribers to keep them excited before launch. Customize the message below, then hit Send.' :'📅 Sends a booking reminder to all customers who made advance bookings. Includes their booking details and 15% discount reminder.'}
        </p>
      </div>

      {/* Form */}
      <div
        className="rounded-2xl p-6 space-y-5"
        style={{
          background: 'linear-gradient(145deg, rgba(255,255,255,0.025) 0%, rgba(0,245,255,0.04) 100%)',
          border: '1px solid rgba(0,245,255,0.1)',
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {campaignType === 'teaser' && (
            <div>
              <label style={labelStyle}>Teaser Number</label>
              <input
                type="number"
                min="1"
                value={form.teaserNumber}
                onChange={(e) => setForm({ ...form, teaserNumber: e.target.value })}
                placeholder="e.g. 1"
                style={inputStyle}
              />
            </div>
          )}
          <div className={campaignType === 'teaser' ? '' : 'md:col-span-2'}>
            <label style={labelStyle}>Email Subject Line</label>
            <input
              type="text"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              style={inputStyle}
            />
          </div>
        </div>

        <div>
          <label style={labelStyle}>Headline (large text in email)</label>
          <input
            type="text"
            value={form.headline}
            onChange={(e) => setForm({ ...form, headline: e.target.value })}
            style={inputStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>Body Message</label>
          <textarea
            rows={4}
            value={form.bodyText}
            onChange={(e) => setForm({ ...form, bodyText: e.target.value })}
            style={{ ...inputStyle, resize: 'vertical' as const }}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label style={labelStyle}>CTA Button Text</label>
            <input
              type="text"
              value={form.ctaText}
              onChange={(e) => setForm({ ...form, ctaText: e.target.value })}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>CTA Button URL</label>
            <input
              type="url"
              value={form.ctaUrl}
              onChange={(e) => setForm({ ...form, ctaUrl: e.target.value })}
              style={inputStyle}
            />
          </div>
        </div>

        {/* Send button */}
        <div className="pt-2">
          <button
            onClick={handleSend}
            disabled={sending}
            className="px-6 py-3 rounded-xl mono text-[11px] font-semibold tracking-widest uppercase transition-all duration-200 disabled:opacity-50"
            style={{
              background: sending
                ? 'rgba(0,245,255,0.08)'
                : 'linear-gradient(135deg, rgba(0,245,255,0.2) 0%, rgba(123,47,255,0.2) 100%)',
              border: '1px solid rgba(0,245,255,0.3)',
              color: '#00f5ff',
              cursor: sending ? 'not-allowed' : 'pointer',
            }}
          >
            {sending ? '⏳ Sending…' : `🚀 Send ${campaignType === 'teaser' ? 'Teaser' : 'Reminder'} Emails`}
          </button>
        </div>
      </div>

      {/* Result */}
      {result && (
        <div
          className="rounded-xl p-5"
          style={{
            background: 'rgba(0,245,255,0.06)',
            border: '1px solid rgba(0,245,255,0.25)',
          }}
        >
          <p className="mono text-[11px] tracking-widest uppercase mb-2" style={{ color: '#00f5ff' }}>
            ✓ Campaign Sent
          </p>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>
            {result.message
              ? result.message
              : `Sent to ${result.sent} of ${result.total} recipient${result.total !== 1 ? 's' : ''}.`}
          </p>
          {result.errors && result.errors.length > 0 && (
            <div className="mt-3">
              <p className="mono text-[10px] tracking-widest uppercase mb-1" style={{ color: 'rgba(255,100,100,0.7)' }}>
                Errors ({result.errors.length})
              </p>
              <ul className="space-y-1">
                {result.errors.map((err, i) => (
                  <li key={i} className="text-xs" style={{ color: 'rgba(255,100,100,0.6)' }}>
                    {err}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {sendError && (
        <div
          className="rounded-xl p-4 mono text-xs"
          style={{ background: 'rgba(220,53,69,0.08)', border: '1px solid rgba(220,53,69,0.25)', color: 'rgba(220,53,69,0.8)' }}
        >
          ⚠ {sendError}
        </div>
      )}
    </div>
  );
}

// ── Traffic Panel ────────────────────────────────────────────────────────────
interface PageViewRow {
  id: string;
  page: string;
  referrer: string | null;
  created_at: string;
}

function TrafficPanel() {
  const [views, setViews] = React.useState<PageViewRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState<string | null>(null);

  React.useEffect(() => {
    async function load() {
      setLoading(true);
      setErr(null);
      try {
        const { data, error } = await supabase
          .from('page_views')
          .select('*')
          .order('created_at', { ascending: false });
        if (error) throw new Error(error.message);
        setViews(data ?? []);
      } catch (e: unknown) {
        setErr(e instanceof Error ? e.message : 'Failed to load traffic data');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const total = views.length;

  // Count views per day for the last 7 days
  const now = new Date();
  const dayLabels: string[] = [];
  const dayCounts: number[] = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    dayLabels.push(label);
    const count = views.filter((v) => {
      const vd = new Date(v.created_at);
      return (
        vd.getFullYear() === d.getFullYear() &&
        vd.getMonth() === d.getMonth() &&
        vd.getDate() === d.getDate()
      );
    }).length;
    dayCounts.push(count);
  }

  const maxCount = Math.max(...dayCounts, 1);

  // Today's count
  const todayCount = dayCounts[dayCounts.length - 1];

  // Unique days with views
  const activeDays = dayCounts.filter((c) => c > 0).length;

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="mono text-[10px] tracking-widest uppercase" style={{ color: 'rgba(168,169,173,0.4)' }}>
          Loading traffic data…
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div
        className="rounded-xl p-4 mono text-xs"
        style={{ background: 'rgba(220,53,69,0.08)', border: '1px solid rgba(220,53,69,0.25)', color: 'rgba(220,53,69,0.8)' }}
      >
        ⚠ {err}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3 md:gap-4">
        {[
          { label: 'Total Visits', value: total, icon: '◈', color: '#00f5ff' },
          { label: 'Today', value: todayCount, icon: '✦', color: '#4A7EC0' },
          { label: 'Active Days', value: activeDays, icon: '◆', color: '#7B2FFF' },
        ].map((stat) => (
          <div
            key={stat.label}
            className="rounded-xl p-4 md:p-5"
            style={{
              background: 'linear-gradient(145deg, rgba(255,255,255,0.03) 0%, rgba(0,245,255,0.05) 100%)',
              border: '1px solid rgba(0,245,255,0.12)',
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm" style={{ color: stat.color }}>{stat.icon}</span>
              <span className="mono text-[9px] tracking-widest uppercase" style={{ color: 'rgba(168,169,173,0.45)' }}>
                {stat.label}
              </span>
            </div>
            <div className="text-2xl md:text-3xl font-bold tracking-tight" style={{ color: '#F5F5F7' }}>
              {stat.value}
            </div>
          </div>
        ))}
      </div>

      {/* Bar chart — last 7 days */}
      <div
        className="rounded-2xl p-6"
        style={{
          background: 'linear-gradient(145deg, rgba(255,255,255,0.025) 0%, rgba(0,245,255,0.04) 100%)',
          border: '1px solid rgba(0,245,255,0.1)',
        }}
      >
        <p className="mono text-[10px] tracking-widest uppercase mb-5" style={{ color: 'rgba(168,169,173,0.5)' }}>
          Visits — Last 7 Days
        </p>
        <div className="flex items-end gap-2 h-32">
          {dayLabels.map((label, i) => {
            const pct = dayCounts[i] / maxCount;
            return (
              <div key={label} className="flex-1 flex flex-col items-center gap-1">
                <span className="mono text-[9px]" style={{ color: 'rgba(168,169,173,0.5)' }}>
                  {dayCounts[i] > 0 ? dayCounts[i] : ''}
                </span>
                <div
                  className="w-full rounded-t-md transition-all duration-300"
                  style={{
                    height: `${Math.max(pct * 96, dayCounts[i] > 0 ? 4 : 2)}px`,
                    background:
                      i === dayLabels.length - 1
                        ? 'linear-gradient(180deg, #00f5ff 0%, rgba(0,245,255,0.3) 100%)'
                        : 'linear-gradient(180deg, rgba(74,126,192,0.7) 0%, rgba(74,126,192,0.2) 100%)',
                  }}
                />
                <span className="mono text-[8px] text-center leading-tight" style={{ color: 'rgba(168,169,173,0.4)' }}>
                  {label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent visits log */}
      {views.length > 0 && (
        <div
          className="rounded-2xl p-6"
          style={{
            background: 'linear-gradient(145deg, rgba(255,255,255,0.025) 0%, rgba(0,245,255,0.04) 100%)',
            border: '1px solid rgba(0,245,255,0.1)',
          }}
        >
          <p className="mono text-[10px] tracking-widest uppercase mb-4" style={{ color: 'rgba(168,169,173,0.5)' }}>
            Recent Visits
          </p>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {views.slice(0, 50).map((v) => (
              <div
                key={v.id}
                className="flex items-center justify-between gap-4 py-2 border-b"
                style={{ borderColor: 'rgba(168,169,173,0.06)' }}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-xs" style={{ color: '#00f5ff' }}>◈</span>
                  <span className="mono text-[11px] truncate" style={{ color: 'rgba(212,212,216,0.7)' }}>
                    {v.page}
                  </span>
                  {v.referrer && (
                    <span className="mono text-[10px] truncate hidden md:block" style={{ color: 'rgba(168,169,173,0.35)' }}>
                      via {v.referrer}
                    </span>
                  )}
                </div>
                <span className="mono text-[10px] shrink-0" style={{ color: 'rgba(168,169,173,0.4)' }}>
                  {new Date(v.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  {' · '}
                  {new Date(v.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {views.length === 0 && (
        <div
          className="rounded-2xl p-12 text-center"
          style={{
            background: 'linear-gradient(145deg, rgba(255,255,255,0.02) 0%, rgba(0,245,255,0.04) 100%)',
            border: '1px solid rgba(0,245,255,0.08)',
          }}
        >
          <div className="text-3xl mb-3" style={{ color: 'rgba(0,245,255,0.2)' }}>◈</div>
          <p className="mono text-[11px] tracking-widest uppercase" style={{ color: 'rgba(168,169,173,0.35)' }}>
            No visits recorded yet
          </p>
          <p className="text-xs mt-2" style={{ color: 'rgba(168,169,173,0.25)' }}>
            Visits will appear here as people browse the site
          </p>
        </div>
      )}
    </div>
  );
}

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; color: string; bg: string }> = {
    pending: { label: 'Pending', color: '#F5A623', bg: 'rgba(245,166,35,0.12)' },
    confirmed: { label: 'Confirmed', color: '#4A7EC0', bg: 'rgba(74,126,192,0.12)' },
    completed: { label: 'Completed', color: '#5CB85C', bg: 'rgba(92,184,92,0.12)' },
    cancelled: { label: 'Cancelled', color: 'rgba(168,169,173,0.6)', bg: 'rgba(168,169,173,0.08)' },
  };
  const s = map[status] ?? map.pending;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full mono text-[10px] font-semibold tracking-widest uppercase"
      style={{ color: s.color, background: s.bg, border: `1px solid ${s.color}33` }}
    >
      <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: s.color }} />
      {s.label}
    </span>
  );
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [pwError, setPwError] = useState(false);
  const [tab, setTab] = useState<TabType>('waitlist');
  const [waitlist, setWaitlist] = useState<WaitlistEntry[]>([]);
  const [bookings, setBookings] = useState<BookingEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailSending, setEmailSending] = useState<Record<string, boolean>>({});
  const [emailStatus, setEmailStatus] = useState<Record<string, 'sent' | 'error'>>({});
  const [showSpamToast, setShowSpamToast] = useState(false);

  // Auto-dismiss spam toast after 8 seconds
  useEffect(() => {
    if (showSpamToast) {
      const timer = setTimeout(() => setShowSpamToast(false), 8000);
      return () => clearTimeout(timer);
    }
  }, [showSpamToast]);

  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || '';

  const sendWaitlistEmail = async (entry: WaitlistEntry) => {
    setEmailSending((prev) => ({ ...prev, [entry.id]: true }));
    setEmailStatus((prev) => { const n = { ...prev }; delete n[entry.id]; return n; });
    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/send-waitlist-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || ''}`,
        },
        body: JSON.stringify({
          email: entry.email,
          location: entry.location,
          vehicleYear: entry.vehicle_year,
          vehicleMake: entry.vehicle_make,
          vehicleModel: entry.vehicle_model,
          vehicleType: entry.vehicle_type,
        }),
      });
      if (!res.ok) throw new Error('Failed');
      setEmailStatus((prev) => ({ ...prev, [entry.id]: 'sent' }));
      setShowSpamToast(true);
    } catch {
      setEmailStatus((prev) => ({ ...prev, [entry.id]: 'error' }));
    } finally {
      setEmailSending((prev) => ({ ...prev, [entry.id]: false }));
    }
  };

  const sendBookingEmail = async (entry: BookingEntry) => {
    if (!entry.email) return;
    setEmailSending((prev) => ({ ...prev, [entry.id]: true }));
    setEmailStatus((prev) => { const n = { ...prev }; delete n[entry.id]; return n; });
    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/send-booking-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || ''}`,
        },
        body: JSON.stringify({
          email: entry.email,
          vehicleYear: entry.vehicle_year,
          vehicleMake: entry.vehicle_make,
          vehicleModel: entry.vehicle_model,
          service: entry.service,
          servicePrice: entry.service_price,
          date: entry.requested_date,
          phone: entry.phone,
          photoFileName: entry.photo_file_name,
        }),
      });
      if (!res.ok) throw new Error('Failed');
      setEmailStatus((prev) => ({ ...prev, [entry.id]: 'sent' }));
      setShowSpamToast(true);
    } catch {
      setEmailStatus((prev) => ({ ...prev, [entry.id]: 'error' }));
    } finally {
      setEmailSending((prev) => ({ ...prev, [entry.id]: false }));
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [wRes, bRes] = await Promise.all([
        supabase.from('waitlist').select('*').order('created_at', { ascending: false }),
        supabase.from('bookings').select('*').order('created_at', { ascending: false }),
      ]);
      if (wRes.error) throw new Error(wRes.error.message);
      if (bRes.error) throw new Error(bRes.error.message);
      setWaitlist(wRes.data ?? []);
      setBookings(bRes.data ?? []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) fetchData();
  }, [authed, fetchData]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (username === ADMIN_USERNAME && password === ADMIN_PASSWORD) {
      setAuthed(true);
      setPwError(false);
    } else {
      setPwError(true);
    }
  };

  // ── Login gate ──────────────────────────────────────────────────────────────
  if (!authed) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: 'linear-gradient(180deg, #080810 0%, #0d0d12 60%, #111114 100%)' }}
      >
        <div
          className="w-full max-w-sm rounded-2xl p-8"
          style={{
            background: 'linear-gradient(145deg, rgba(255,255,255,0.03) 0%, rgba(45,95,154,0.08) 100%)',
            border: '1px solid rgba(45,95,154,0.25)',
            boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
          }}
        >
          {/* Logo mark */}
          <div className="text-center mb-8">
            <div
              className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4"
              style={{ background: 'rgba(74,126,192,0.12)', border: '1px solid rgba(74,126,192,0.3)' }}
            >
              <span style={{ color: '#4A7EC0', fontSize: 20 }}>◆</span>
            </div>
            <h1 className="text-xl font-bold tracking-tight" style={{ color: '#F5F5F7' }}>
              Admin Access
            </h1>
            <p className="mono text-[10px] tracking-widest uppercase mt-1" style={{ color: 'rgba(168,169,173,0.45)' }}>
              True Blü Mobile Detail
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="mono text-[10px] tracking-widest uppercase block mb-2" style={{ color: 'rgba(168,169,173,0.5)' }}>
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => { setUsername(e.target.value); setPwError(false); }}
                placeholder="Enter username"
                className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: pwError ? '1px solid rgba(220,53,69,0.6)' : '1px solid rgba(74,126,192,0.2)',
                  color: '#F5F5F7',
                  fontSize: 16,
                }}
                autoFocus
              />
            </div>
            <div>
              <label className="mono text-[10px] tracking-widest uppercase block mb-2" style={{ color: 'rgba(168,169,173,0.5)' }}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setPwError(false); }}
                placeholder="Enter admin password"
                className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: pwError ? '1px solid rgba(220,53,69,0.6)' : '1px solid rgba(74,126,192,0.2)',
                  color: '#F5F5F7',
                  fontSize: 16,
                }}
              />
              {pwError && (
                <p className="mono text-[10px] tracking-wider mt-2" style={{ color: 'rgba(220,53,69,0.8)' }}>
                  Incorrect username or password. Try again.
                </p>
              )}
            </div>
            <button
              type="submit"
              className="w-full py-3 rounded-xl mono text-[11px] font-semibold tracking-widest uppercase transition-all duration-200"
              style={{
                background: 'linear-gradient(135deg, rgba(45,95,154,0.4) 0%, rgba(45,95,154,0.2) 100%)',
                border: '1px solid rgba(74,126,192,0.4)',
                color: '#4A7EC0',
              }}
            >
              Enter Dashboard
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link href="/home" className="mono text-[10px] tracking-wider uppercase" style={{ color: 'rgba(168,169,173,0.35)' }}>
              ← Back to site
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ── Dashboard ───────────────────────────────────────────────────────────────
  const activeData = tab === 'waitlist' ? waitlist : bookings;
  const waitlistCount = waitlist.length;
  const bookingsCount = bookings.length;
  const pendingCount = bookings.filter((b) => b.status === 'pending').length;

  return (
    <div
      className="min-h-screen"
      style={{ background: 'linear-gradient(180deg, #080810 0%, #0d0d12 60%, #111114 100%)' }}
    >
      {/* Spam folder toast notification */}
      <div
        style={{
          position: 'fixed',
          bottom: '24px',
          left: '50%',
          transform: showSpamToast ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(120px)',
          opacity: showSpamToast ? 1 : 0,
          transition: 'transform 0.35s cubic-bezier(0.34,1.56,0.64,1), opacity 0.3s ease',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          background: 'linear-gradient(135deg, #0d0d2b 0%, #111130 100%)',
          border: '1px solid rgba(74,126,192,0.5)',
          boxShadow: '0 0 24px rgba(74,126,192,0.15), 0 8px 32px rgba(0,0,0,0.6)',
          padding: '14px 18px',
          maxWidth: '380px',
          width: 'calc(100vw - 48px)',
          pointerEvents: showSpamToast ? 'auto' : 'none',
          borderRadius: '8px',
        }}
      >
        <div style={{ fontSize: '20px', flexShrink: 0 }}>📬</div>
        <div style={{ flex: 1 }}>
          <p style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#4A7EC0', marginBottom: '2px' }}>
            Remind Customer: Check Spam
          </p>
          <p style={{ fontSize: '11px', color: '#a0b0d8', lineHeight: 1.5 }}>
            Email sent — ask the customer to check their spam or junk folder if they don&apos;t see it.
          </p>
        </div>
        <button
          onClick={() => setShowSpamToast(false)}
          style={{ background: 'none', border: 'none', color: '#7b8ab8', cursor: 'pointer', fontSize: '18px', flexShrink: 0, padding: '2px', lineHeight: 1 }}
          aria-label="Dismiss"
        >
          ×
        </button>
      </div>

      {/* Top bar */}
      <div
        className="sticky top-0 z-20 border-b"
        style={{
          background: 'rgba(8,8,16,0.92)',
          backdropFilter: 'blur(16px)',
          borderColor: 'rgba(168,169,173,0.08)',
        }}
      >
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
              style={{ background: 'rgba(74,126,192,0.12)', border: '1px solid rgba(74,126,192,0.3)' }}
            >
              <span style={{ color: '#4A7EC0', fontSize: 14 }}>◆</span>
            </div>
            <div>
              <h1 className="text-sm font-bold tracking-tight" style={{ color: '#F5F5F7' }}>
                Admin Dashboard
              </h1>
              <p className="mono text-[9px] tracking-widest uppercase" style={{ color: 'rgba(168,169,173,0.4)' }}>
                Vertex Premium Mobile Detail
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchData}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg mono text-[10px] tracking-widest uppercase transition-all"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(168,169,173,0.12)',
                color: 'rgba(168,169,173,0.6)',
              }}
            >
              ↻ Refresh
            </button>
            <Link
              href="/home"
              className="mono text-[10px] tracking-widest uppercase"
              style={{ color: 'rgba(168,169,173,0.35)' }}
            >
              ← Site
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3 md:gap-4 mb-8">
          {[
            { label: 'Waitlist', value: waitlistCount, icon: '◈', color: '#4A7EC0' },
            { label: 'Bookings', value: bookingsCount, icon: '✦', color: '#4A7EC0' },
            { label: 'Pending', value: pendingCount, icon: '◆', color: '#F5A623' },
          ].map((stat) => (
            <div
              key={stat.label}
              className="rounded-xl p-4 md:p-5"
              style={{
                background: 'linear-gradient(145deg, rgba(255,255,255,0.03) 0%, rgba(45,95,154,0.06) 100%)',
                border: '1px solid rgba(45,95,154,0.15)',
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm" style={{ color: stat.color }}>{stat.icon}</span>
                <span className="mono text-[9px] tracking-widest uppercase" style={{ color: 'rgba(168,169,173,0.45)' }}>
                  {stat.label}
                </span>
              </div>
              <div className="text-2xl md:text-3xl font-bold tracking-tight" style={{ color: '#F5F5F7' }}>
                {loading ? '—' : stat.value}
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div
          className="flex gap-1 p-1 rounded-xl mb-6 w-fit flex-wrap"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(168,169,173,0.08)' }}
        >
          {(['waitlist', 'bookings', 'campaigns', 'content', 'traffic'] as TabType[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className="px-4 py-2 rounded-lg mono text-[10px] font-semibold tracking-widest uppercase transition-all duration-200"
              style={
                tab === t
                  ? {
                      background: t === 'campaigns' ?'linear-gradient(135deg, rgba(0,245,255,0.15) 0%, rgba(123,47,255,0.15) 100%)'
                        : t === 'content' ?'linear-gradient(135deg, rgba(255,0,255,0.15) 0%, rgba(123,47,255,0.15) 100%)'
                        : t === 'traffic' ? 'linear-gradient(135deg, rgba(0,245,255,0.12) 0%, rgba(74,126,192,0.2) 100%)'
                        :'linear-gradient(135deg, rgba(45,95,154,0.3) 0%, rgba(45,95,154,0.15) 100%)',
                      border: t === 'campaigns' ?'1px solid rgba(0,245,255,0.3)'
                        : t === 'content' ?'1px solid rgba(255,0,255,0.3)'
                        : t === 'traffic' ? '1px solid rgba(0,245,255,0.25)'
                        :'1px solid rgba(74,126,192,0.35)',
                      color: t === 'campaigns' ? '#00f5ff' : t === 'content' ? '#ff00ff' : t === 'traffic' ? '#00f5ff' : '#4A7EC0',
                    }
                  : {
                      background: 'transparent',
                      border: '1px solid transparent',
                      color: 'rgba(168,169,173,0.45)',
                    }
              }
            >
              {t === 'waitlist'
                ? `Waitlist (${waitlistCount})`
                : t === 'bookings'
                ? `Bookings (${bookingsCount})`
                : t === 'campaigns' ?'🚀 Email Campaigns' : t === 'traffic' ? '📊 Site Traffic' :'📸 Reviews & Photos'}
            </button>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div
            className="rounded-xl p-4 mb-6 mono text-xs"
            style={{ background: 'rgba(220,53,69,0.08)', border: '1px solid rgba(220,53,69,0.25)', color: 'rgba(220,53,69,0.8)' }}
          >
            ⚠ {error}
          </div>
        )}

        {/* Campaigns tab */}
        {tab === 'campaigns' && (
          <CampaignPanel supabaseUrl={process.env.NEXT_PUBLIC_SUPABASE_URL || ''} />
        )}

        {/* Content tab */}
        {tab === 'content' && (
          <ReviewsManager />
        )}

        {/* Traffic tab */}
        {tab === 'traffic' && (
          <TrafficPanel />
        )}

        {/* Loading */}
        {tab !== 'campaigns' && tab !== 'content' && tab !== 'traffic' && loading && (
          <div className="text-center py-16">
            <div className="mono text-[10px] tracking-widest uppercase" style={{ color: 'rgba(168,169,173,0.4)' }}>
              Loading submissions…
            </div>
          </div>
        )}

        {/* Empty */}
        {tab !== 'campaigns' && tab !== 'content' && tab !== 'traffic' && !loading && !error && activeData.length === 0 && (
          <div
            className="rounded-2xl p-12 text-center"
            style={{
              background: 'linear-gradient(145deg, rgba(255,255,255,0.02) 0%, rgba(45,95,154,0.04) 100%)',
              border: '1px solid rgba(45,95,154,0.1)',
            }}
          >
            <div className="text-3xl mb-3" style={{ color: 'rgba(74,126,192,0.3)' }}>◈</div>
            <p className="mono text-[11px] tracking-widest uppercase" style={{ color: 'rgba(168,169,173,0.35)' }}>
              No {tab} submissions yet
            </p>
          </div>
        )}

        {/* Waitlist table */}
        {!loading && !error && tab === 'waitlist' && waitlist.length > 0 && (
          <div className="space-y-3">
            {waitlist.map((entry) => (
              <div
                key={entry.id}
                className="rounded-xl p-4 md:p-5"
                style={{
                  background: 'linear-gradient(145deg, rgba(255,255,255,0.025) 0%, rgba(45,95,154,0.06) 100%)',
                  border: '1px solid rgba(45,95,154,0.15)',
                }}
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                  {/* Left: contact + vehicle */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="text-sm font-semibold" style={{ color: '#F5F5F7' }}>
                        {entry.email}
                      </span>
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full mono text-[10px] font-semibold tracking-widest uppercase"
                        style={{ color: '#4A7EC0', background: 'rgba(74,126,192,0.1)', border: '1px solid rgba(74,126,192,0.25)' }}
                      >
                        Waitlist
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      <span className="mono text-[10px] tracking-wider" style={{ color: 'rgba(168,169,173,0.5)' }}>
                        📍 {entry.location}
                      </span>
                      {(entry.vehicle_year || entry.vehicle_make || entry.vehicle_model) && (
                        <span className="mono text-[10px] tracking-wider" style={{ color: 'rgba(168,169,173,0.5)' }}>
                          🚗 {[entry.vehicle_year, entry.vehicle_make, entry.vehicle_model].filter(Boolean).join(' ')}
                          {entry.vehicle_type ? ` · ${entry.vehicle_type}` : ''}
                        </span>
                      )}
                    </div>
                    {entry.notes && (
                      <p className="text-xs mt-2 leading-relaxed" style={{ color: 'rgba(212,212,216,0.5)' }}>
                        {entry.notes}
                      </p>
                    )}
                  </div>
                  {/* Right: timestamp + send email button */}
                  <div className="shrink-0 flex flex-col items-end gap-2">
                    <div className="mono text-[10px] tracking-wider" style={{ color: 'rgba(168,169,173,0.4)' }}>
                      {formatDate(entry.created_at)}
                    </div>
                    <button
                      onClick={() => sendWaitlistEmail(entry)}
                      disabled={emailSending[entry.id]}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg mono text-[10px] font-semibold tracking-widest uppercase transition-all duration-200 disabled:opacity-50"
                      style={
                        emailStatus[entry.id] === 'sent'
                          ? { background: 'rgba(92,184,92,0.15)', border: '1px solid rgba(92,184,92,0.35)', color: '#5CB85C', cursor: 'default' }
                          : emailStatus[entry.id] === 'error'
                          ? { background: 'rgba(220,53,69,0.1)', border: '1px solid rgba(220,53,69,0.3)', color: 'rgba(220,53,69,0.8)', cursor: 'pointer' }
                          : { background: 'rgba(74,126,192,0.1)', border: '1px solid rgba(74,126,192,0.3)', color: '#4A7EC0', cursor: emailSending[entry.id] ? 'not-allowed' : 'pointer' }
                      }
                    >
                      {emailSending[entry.id]
                        ? '⏳ Sending…'
                        : emailStatus[entry.id] === 'sent' ?'✓ Email Sent'
                        : emailStatus[entry.id] === 'error' ?'⚠ Retry Email' :'✉ Send Confirmation'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Bookings table */}
        {!loading && !error && tab === 'bookings' && bookings.length > 0 && (
          <div className="space-y-3">
            {bookings.map((entry) => (
              <div
                key={entry.id}
                className="rounded-xl p-4 md:p-5"
                style={{
                  background: 'linear-gradient(145deg, rgba(255,255,255,0.025) 0%, rgba(45,95,154,0.06) 100%)',
                  border: '1px solid rgba(45,95,154,0.15)',
                }}
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                  {/* Left: contact + details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="text-sm font-semibold" style={{ color: '#F5F5F7' }}>
                        {entry.email ?? entry.phone ?? 'No contact info'}
                      </span>
                      {entry.phone && entry.email && (
                        <span className="mono text-[10px]" style={{ color: 'rgba(168,169,173,0.45)' }}>
                          📞 {entry.phone}
                        </span>
                      )}
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full mono text-[10px] font-semibold tracking-widest uppercase"
                        style={{
                          color: entry.submission_type === 'quote' ? '#F5A623' : '#4A7EC0',
                          background: entry.submission_type === 'quote' ? 'rgba(245,166,35,0.1)' : 'rgba(74,126,192,0.1)',
                          border: `1px solid ${entry.submission_type === 'quote' ? 'rgba(245,166,35,0.25)' : 'rgba(74,126,192,0.25)'}`,
                        }}
                      >
                        {entry.submission_type === 'quote' ? '📸 Quote' : '🗓 Booking'}
                      </span>
                      <StatusBadge status={entry.status} />
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {entry.service && (
                        <span className="mono text-[10px] tracking-wider" style={{ color: 'rgba(168,169,173,0.5)' }}>
                          ✦ {entry.service}{entry.service_price ? ` · ${entry.service_price}` : ''}
                        </span>
                      )}
                      {(entry.vehicle_year || entry.vehicle_make || entry.vehicle_model) && (
                        <span className="mono text-[10px] tracking-wider" style={{ color: 'rgba(168,169,173,0.5)' }}>
                          🚗 {[entry.vehicle_year, entry.vehicle_make, entry.vehicle_model].filter(Boolean).join(' ')}
                        </span>
                      )}
                      {entry.requested_date && (
                        <span className="mono text-[10px] tracking-wider" style={{ color: 'rgba(168,169,173,0.5)' }}>
                          📅 {entry.requested_date}
                        </span>
                      )}
                      {entry.photo_file_name && (
                        <span className="mono text-[10px] tracking-wider" style={{ color: 'rgba(168,169,173,0.5)' }}>
                          🖼 {entry.photo_file_name}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Right: timestamp + send email button */}
                  <div className="shrink-0 flex flex-col items-end gap-2">
                    <div className="mono text-[10px] tracking-wider" style={{ color: 'rgba(168,169,173,0.4)' }}>
                      {formatDate(entry.created_at)}
                    </div>
                    {entry.email && entry.submission_type !== 'quote' && (
                      <button
                        onClick={() => sendBookingEmail(entry)}
                        disabled={emailSending[entry.id]}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg mono text-[10px] font-semibold tracking-widest uppercase transition-all duration-200 disabled:opacity-50"
                        style={
                          emailStatus[entry.id] === 'sent'
                            ? { background: 'rgba(92,184,92,0.15)', border: '1px solid rgba(92,184,92,0.35)', color: '#5CB85C', cursor: 'default' }
                            : emailStatus[entry.id] === 'error'
                            ? { background: 'rgba(220,53,69,0.1)', border: '1px solid rgba(220,53,69,0.3)', color: 'rgba(220,53,69,0.8)', cursor: 'pointer' }
                            : { background: 'rgba(74,126,192,0.1)', border: '1px solid rgba(74,126,192,0.3)', color: '#4A7EC0', cursor: emailSending[entry.id] ? 'not-allowed' : 'pointer' }
                        }
                      >
                        {emailSending[entry.id]
                          ? '⏳ Sending…'
                          : emailStatus[entry.id] === 'sent' ?'✓ Email Sent'
                          : emailStatus[entry.id] === 'error' ?'⚠ Retry Email' :'✉ Send Confirmation'}
                      </button>
                    )}
                    {entry.submission_type === 'quote' && (
                      <span
                        className="px-3 py-1.5 rounded-lg mono text-[10px] tracking-widest uppercase"
                        style={{ background: 'rgba(245,166,35,0.06)', border: '1px solid rgba(245,166,35,0.15)', color: 'rgba(245,166,35,0.45)' }}
                      >
                        📸 Quote — no email
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
